import React from 'react';
import { Card, CardContent, Typography, Box, Stack, Paper, Skeleton } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';

// --- (Child Component) SummaryRow ---
// This component remains unchanged.
interface SummaryRowProps {
    icon: string;
    label: [string, string];
    value: string;
    change?: string;
    changeType?: 'positive' | 'negative';
    changeDescription?: string;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ icon, label, value, change, changeType, changeDescription }) => {
    const isNegative = changeType === 'negative';
    const changeColor = isNegative ? '#CA2B20' : '#03A65A';
    const ChangeIcon = isNegative ? ArrowUpward : ArrowDownward;
    const rotationTransform = isNegative ? 'rotate(-120deg)' : 'rotate(120deg)';

    return (
        <Paper elevation={0} sx={{ p: '16px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#F9FAFB' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#FFFFFF',
                }}>
                    <Box component="img" src={icon} alt={label.join(' ')} sx={{ width: 20, height: 20 }} />
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.2, fontFamily: 'inherit' }}>{label[0]}</Typography>
                    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.2, fontFamily: 'inherit' }}>{label[1]}</Typography>
                </Box>
            </Box>
            
            <Box sx={{ textAlign: 'right' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Typography sx={{ fontSize:'16px', fontWeight: 600, fontFamily: 'inherit' }}>{value}</Typography>
                    {change && <ChangeIcon sx={{ color: changeColor, fontSize: '18px', transform: rotationTransform }} />}
                </Box>
                {change && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', color: changeColor, mt: -0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 500, fontFamily: 'inherit' }}>{change}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontFamily: 'inherit' }}>{changeDescription}</Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
}

// --- (Main Component) LLMSummary ---

// 1. Define the shape of the data coming from the backend
interface SummaryData {
    avgTokensPerMessage: number;
    avgCostPerMessage: number;
    p90TimeToFirstTokenMs: number;
    p90CompletionTimeSeconds: number;
    avgTokensPerMessageChange: number | null;
    avgCostPerMessageChange: number | null;
    p90TimeToFirstTokenMsChange: number | null;
    p90CompletionTimeSecondsChange: number | null;
}

// 2. Update component props to accept backend data
interface LLMSummaryProps {
    summaryData?: SummaryData;
    isLoading?: boolean;
}

const LLMSummary: React.FC<LLMSummaryProps> = ({ summaryData, isLoading }) => {
    
    // 3. Create the display data by mapping backend props to the format needed by SummaryRow
    const displayData = summaryData ? [
        { 
            icon: '/icons/llm-summary-1.svg', 
            label: ['Avg.', 'tokens per message'] as [string, string], 
            value: summaryData.avgTokensPerMessage.toFixed(0),
            change: summaryData.avgTokensPerMessageChange ? `${Math.abs(summaryData.avgTokensPerMessageChange).toFixed(2)}%` : undefined,
            changeType: (summaryData.avgTokensPerMessageChange ?? 0) > 0 ? 'negative' as const : 'positive' as const,
            changeDescription: 'vs last month'
        },
        { 
            icon: '/icons/llm-summary-2.svg', 
            label: ['Avg.', 'cost per message'] as [string, string], 
            value: `$${summaryData.avgCostPerMessage.toFixed(5)}`, 
            change: summaryData.avgCostPerMessageChange ? `${Math.abs(summaryData.avgCostPerMessageChange).toFixed(2)}%` : undefined,
            changeType: (summaryData.avgCostPerMessageChange ?? 0) > 0 ? 'negative' as const : 'positive' as const,
            changeDescription: 'vs last month'
        },
        { 
            icon: '/icons/llm-summary-3.svg', 
            label: ['90th', 'percentile time to first token'] as [string, string], 
            value: `${summaryData.p90TimeToFirstTokenMs.toFixed(0)}ms`,
            change: summaryData.p90TimeToFirstTokenMsChange ? `${Math.abs(summaryData.p90TimeToFirstTokenMsChange).toFixed(2)}%` : undefined,
            changeType: (summaryData.p90TimeToFirstTokenMsChange ?? 0) > 0 ? 'negative' as const : 'positive' as const,
            changeDescription: 'vs last month'
        },
        { 
            icon: '/icons/llm-summary-4.svg', 
            label: ['90th', 'percentile completion time'] as [string, string], 
            value: `${summaryData.p90CompletionTimeSeconds.toFixed(1)}s`, 
            change: summaryData.p90CompletionTimeSecondsChange ? `${Math.abs(summaryData.p90CompletionTimeSecondsChange).toFixed(2)}%` : undefined,
            changeType: (summaryData.p90CompletionTimeSecondsChange ?? 0) > 0 ? 'negative' as const : 'positive' as const,
            changeDescription: 'vs last month'
        },
    ] : [];

    return (
        <Card elevation={0} sx={{ borderRadius: '16px', height: '100%', fontFamily: 'Inter' }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: '600', fontSize: '16px', mb: 2, fontFamily: 'inherit' }}>
                    Summary
                </Typography>
                <Stack spacing={2}>
                    {/* 4. Conditionally render Skeletons or the actual data rows */}
                    {isLoading ? (
                         // Render 4 skeleton loaders
                        [...Array(4)].map((_, index) => (
                           <Skeleton key={index} variant="rectangular" sx={{ borderRadius: '14px', height: '72px' }} />
                        ))
                    ) : (
                        // Render the data-driven rows
                        displayData.map(item => <SummaryRow key={item.label.join(' ')} {...item} />)
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

export default LLMSummary;



// import React from 'react';
// import { Card, CardContent, Typography, Box, Stack, Paper, Skeleton } from '@mui/material';
// import { ArrowUpward, ArrowDownward } from '@mui/icons-material';

// // --- (Child Component) SummaryRow ---
// // This component remains unchanged as it's already flexible.
// interface SummaryRowProps {
//     icon: string;
//     label: [string, string];
//     value: string;
//     change?: string;
//     changeType?: 'positive' | 'negative';
//     changeDescription?: string;
// }

// const SummaryRow: React.FC<SummaryRowProps> = ({ icon, label, value, change, changeType, changeDescription }) => {
//     const isNegative = changeType === 'negative';
//     const changeColor = isNegative ? '#CA2B20' : '#03A65A';
//     const ChangeIcon = isNegative ? ArrowUpward : ArrowDownward;
//     const rotationTransform = isNegative ? 'rotate(-120deg)' : 'rotate(120deg)';

//     return (
//         <Paper elevation={0} sx={{ p: '16px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#F9FAFB' }}>
//             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
//                 <Box sx={{
//                     width: 40,
//                     height: 40,
//                     borderRadius: '8px',
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'center',
//                     bgcolor: '#FFFFFF',
//                 }}>
//                     <Box component="img" src={icon} alt={label.join(' ')} sx={{ width: 20, height: 20 }} />
//                 </Box>
//                 <Box>
//                     <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.2, fontFamily: 'inherit' }}>{label[0]}</Typography>
//                     <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.2, fontFamily: 'inherit' }}>{label[1]}</Typography>
//                 </Box>
//             </Box>
            
//             <Box sx={{ textAlign: 'right' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
//                     <Typography sx={{ fontSize:'16px', fontWeight: 600, fontFamily: 'inherit' }}>{value}</Typography>
//                     {change && <ChangeIcon sx={{ color: changeColor, fontSize: '18px', transform: rotationTransform }} />}
//                 </Box>
//                 {change && (
//                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', color: changeColor, mt: -0.5 }}>
//                         <Typography variant="caption" sx={{ fontWeight: 500, fontFamily: 'inherit' }}>{change}</Typography>
//                         <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontFamily: 'inherit' }}>{changeDescription}</Typography>
//                     </Box>
//                 )}
//             </Box>
//         </Paper>
//     );
// }

// // --- (Main Component) LLMSummary ---

// // 1. Define the shape of the data coming from the backend
// interface SummaryData {
//     avgTokensPerMessage: number;
//     avgCostPerMessage: number;
//     p90TimeToFirstTokenMs: number;
//     p90CompletionTimeSeconds: number;
//     avgTokensPerMessageChange: number | null;
//     avgCostPerMessageChange: number | null;
//     p90TimeToFirstTokenMsChange: number | null;
//     p90CompletionTimeSecondsChange: number | null;
// }

// // 2. Update component props to accept backend data
// interface LLMSummaryProps {
//     summaryData?: SummaryData;
//     isLoading?: boolean;
// }

// // Helper to format percentage change, returns undefined if no change
// const formatPercentageChange = (current: number, change: number | null): string | undefined => {
//     if (change === null || change === 0) {
//         return undefined;
//     }
//     const previous = current - change;
//     if (previous === 0) {
//         return '100.00%';
//     }
//     const percentage = (change / Math.abs(previous)) * 100;
//     return `${Math.abs(percentage).toFixed(2)}%`;
// };


// const LLMSummary: React.FC<LLMSummaryProps> = ({ summaryData, isLoading }) => {
    
//     // 3. Create the display data by mapping backend props to the format needed by SummaryRow
//     const displayData = summaryData ? [
//         { 
//             icon: '/icons/llm-summary-1.svg', 
//             label: ['Avg.', 'tokens per message'] as [string, string], 
//             value: summaryData.avgTokensPerMessage.toFixed(0),
//             change: formatPercentageChange(summaryData.avgTokensPerMessage, summaryData.avgTokensPerMessageChange),
//             changeType: (summaryData.avgTokensPerMessageChange ?? 0) > 0 ? 'negative' as const : 'positive' as const,
//             changeDescription: 'vs last month'
//         },
//         { 
//             icon: '/icons/llm-summary-2.svg', 
//             label: ['Avg.', 'cost per message'] as [string, string], 
//             value: `$${summaryData.avgCostPerMessage.toFixed(5)}`, 
//             change: formatPercentageChange(summaryData.avgCostPerMessage, summaryData.avgCostPerMessageChange),
//             changeType: (summaryData.avgCostPerMessageChange ?? 0) > 0 ? 'negative' as const : 'positive' as const,
//             changeDescription: 'vs last month'
//         },
//         { 
//             icon: '/icons/llm-summary-3.svg', 
//             label: ['90th', 'percentile time to first token'] as [string, string], 
//             value: `${summaryData.p90TimeToFirstTokenMs.toFixed(0)}ms`,
//             change: formatPercentageChange(summaryData.p90TimeToFirstTokenMs, summaryData.p90TimeToFirstTokenMsChange),
//             changeType: (summaryData.p90TimeToFirstTokenMsChange ?? 0) > 0 ? 'negative' as const : 'positive' as const,
//             changeDescription: 'vs last month'
//         },
//         { 
//             icon: '/icons/llm-summary-4.svg', 
//             label: ['90th', 'percentile completion time'] as [string, string], 
//             value: `${summaryData.p90CompletionTimeSeconds.toFixed(1)}s`, 
//             change: formatPercentageChange(summaryData.p90CompletionTimeSeconds, summaryData.p90CompletionTimeSecondsChange),
//             changeType: (summaryData.p90CompletionTimeSecondsChange ?? 0) > 0 ? 'negative' as const : 'positive' as const,
//             changeDescription: 'vs last month'
//         },
//     ] : [];

//     return (
//         <Card elevation={0} sx={{ borderRadius: '16px', height: '100%', fontFamily: 'Inter' }}>
//             <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//                 <Typography variant="h6" component="h3" sx={{ fontWeight: '600', fontSize: '16px', mb: 2, fontFamily: 'inherit' }}>
//                     Summary
//                 </Typography>
//                 <Stack spacing={2}>
//                     {/* 4. Conditionally render Skeletons or the actual data rows */}
//                     {isLoading ? (
//                          // Render 4 skeleton loaders
//                         [...Array(4)].map((_, index) => (
//                            <Skeleton key={index} variant="rectangular" sx={{ borderRadius: '14px', height: '72px' }} />
//                         ))
//                     ) : (
//                         // Render the data-driven rows
//                         displayData.map(item => <SummaryRow key={item.label.join(' ')} {...item} />)
//                     )}
//                 </Stack>
//             </CardContent>
//         </Card>
//     );
// }

// export default LLMSummary;

// import React from 'react';
// import { Card, CardContent, Typography, Box, Stack, Paper, Skeleton } from '@mui/material';
// import { ArrowUpward, ArrowDownward } from '@mui/icons-material';

// // --- (Child Component) SummaryRow ---
// // This component remains unchanged as it's already flexible.
// interface SummaryRowProps {
//     icon: string;
//     label: [string, string];
//     value: string;
//     change?: string;
//     changeType?: 'positive' | 'negative';
//     changeDescription?: string;
// }

// const SummaryRow: React.FC<SummaryRowProps> = ({ icon, label, value, change, changeType, changeDescription }) => {
//     const isNegative = changeType === 'negative';
//     const changeColor = isNegative ? '#CA2B20' : '#03A65A';
//     const ChangeIcon = isNegative ? ArrowUpward : ArrowDownward;
//     const rotationTransform = isNegative ? 'rotate(-120deg)' : 'rotate(120deg)';

//     return (
//         <Paper elevation={0} sx={{ p: '16px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#F9FAFB' }}>
//             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
//                 <Box sx={{
//                     width: 40,
//                     height: 40,
//                     borderRadius: '8px',
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'center',
//                     bgcolor: '#FFFFFF',
//                 }}>
//                     <Box component="img" src={icon} alt={label.join(' ')} sx={{ width: 20, height: 20 }} />
//                 </Box>
//                 <Box>
//                     <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.2, fontFamily: 'inherit' }}>{label[0]}</Typography>
//                     <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.2, fontFamily: 'inherit' }}>{label[1]}</Typography>
//                 </Box>
//             </Box>
            
//             <Box sx={{ textAlign: 'right' }}>
//                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
//                     <Typography sx={{ fontSize:'16px', fontWeight: 600, fontFamily: 'inherit' }}>{value}</Typography>
//                     {change && <ChangeIcon sx={{ color: changeColor, fontSize: '18px', transform: rotationTransform }} />}
//                 </Box>
//                 {change && (
//                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', color: changeColor, mt: -0.5 }}>
//                         <Typography variant="caption" sx={{ fontWeight: 500, fontFamily: 'inherit' }}>{change}</Typography>
//                         <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontFamily: 'inherit' }}>{changeDescription}</Typography>
//                     </Box>
//                 )}
//             </Box>
//         </Paper>
//     );
// }

// // --- (Main Component) LLMSummary ---

// // 1. Define the shape of the data coming from the backend
// interface SummaryData {
//     avgTokensPerMessage: number;
//     avgCostPerMessage: number;
//     p90TimeToFirstTokenMs: number;
//     p90CompletionTimeSeconds: number;
// }

// // 2. Update component props to accept backend data
// interface LLMSummaryProps {
//     summaryData?: SummaryData;
//     isLoading?: boolean;
// }

// const LLMSummary: React.FC<LLMSummaryProps> = ({ summaryData, isLoading }) => {
    
//     // 3. Create the display data by mapping backend props to the format needed by SummaryRow
//     const displayData = summaryData ? [
//         { 
//             icon: '/icons/llm-summary-1.svg', 
//             label: ['Avg.', 'tokens per message'] as [string, string], 
//             value: summaryData.avgTokensPerMessage.toFixed(0), 
//             // NOTE: The current API does not provide change data for these metrics.
//         },
//         { 
//             icon: '/icons/llm-summary-2.svg', 
//             label: ['Avg.', 'cost per message'] as [string, string], 
//             // Format to 5 decimal places for small costs
//             value: `$${summaryData.avgCostPerMessage.toFixed(5)}`, 
//         },
//         { 
//             icon: '/icons/llm-summary-3.svg', 
//             label: ['90th', 'percentile time to first token'] as [string, string], 
//             value: `${summaryData.p90TimeToFirstTokenMs.toFixed(0)}ms`,
//         },
//         { 
//             icon: '/icons/llm-summary-4.svg', 
//             label: ['90th', 'percentile completion time'] as [string, string], 
//             value: `${summaryData.p90CompletionTimeSeconds.toFixed(1)}s`, 
//         },
//     ] : [];

//     return (
//         <Card elevation={0} sx={{ borderRadius: '16px', height: '100%', fontFamily: 'Inter' }}>
//             <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//                 <Typography variant="h6" component="h3" sx={{ fontWeight: '600', fontSize: '16px', mb: 2, fontFamily: 'inherit' }}>
//                     Summary
//                 </Typography>
//                 <Stack spacing={2}>
//                     {/* 4. Conditionally render Skeletons or the actual data rows */}
//                     {isLoading ? (
//                          // Render 4 skeleton loaders
//                         [...Array(4)].map((_, index) => (
//                            <Skeleton key={index} variant="rectangular" sx={{ borderRadius: '14px', height: '72px' }} />
//                         ))
//                     ) : (
//                         // Render the data-driven rows
//                         displayData.map(item => <SummaryRow key={item.label.join(' ')} {...item} />)
//                     )}
//                 </Stack>
//             </CardContent>
//         </Card>
//     );
// }

// export default LLMSummary;